import React, {useEffect, useRef} from "react";
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Link,
    Paper,
    Typography,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import {SectionTitle} from "../../../export/advanced_export";
import EnhancedStepper from "../../../../../../components/EnhancedStepper";
import {
    escapeSpaces, importRetailerProducts, importRetailerProductsStatus,
    importSupplierProducts,
    importSupplierProductsStatus, SUPPLIER_SYNDICATION_ENABLED,
    updateImportLinkTarget,
} from "../../../../../../utils/utils";
import axios from "axios";
import {SaveProfile} from "../../../export/components/EnhancedProfile";
import {useHistory} from "react-router";
import ErrorIcon from "@mui/icons-material/Error";
import { DialogHeader, pageContentGap } from "../../../../../../theme";
import type { ImportProfile } from "../../../../types";
import type { ParsedField, WizardSharedProps } from "../types";

// region TypeScript Interfaces
type ReviewProps = WizardSharedProps;

interface ImportStatus {
    Complete: boolean;
    ImportedRecords: number;
    FailedRecords: number;
    ErrorMessage: string;
    ErrorReport: string;
}
// endregion

export const formatProfileToColumns = (profile: ImportProfile, parsedFields: ParsedField[]) => {
    if(!parsedFields || Object.keys(profile.AttributeMappings).length === 0) return [];

    // const headers = parsedFields ? parsedFields.map(r => r.field) : [];
    return Object.entries(profile.AttributeMappings)
        .filter(([key, value]) => value != null)
        .map(([key, value]) => {
            return {
                SourceField: "{" + value + "}",
                TargetField: key
            }
        });
}

export const formatProfileListMappingsToRules = (profile: ImportProfile, parsedFields: ParsedField[]) => {
    if(!profile.ListMappings || Object.keys(profile.ListMappings).length === 0) return [];

    const headers = parsedFields ? parsedFields.map(r => r.field) : [];
    let listRules: any[] = [];
    Object.entries(profile.ListMappings).forEach(([key, value]) => {
        listRules.push(...Object.entries(value as Record<string, unknown>).map(([listKey, listValue]) => {
            return {
                SourceField: `"${listKey}"`,
                TargetField: profile.AttributeMappings[key],
                Condition: {
                    Field: (escapeSpaces as any)(profile.AttributeMappings[key], headers),
                    Operator: "equals",
                    Value: `"${listValue}"`
                }
            }
        }));
    })

    return listRules;
}

export const formatProfileCategoryMappingsToRules = (profile: ImportProfile, parsedFields: ParsedField[]) => {
    if(!profile.CategoryMappings || Object.keys(profile.CategoryMappings).length === 0) return [];

    const headers = parsedFields ? parsedFields.map(r => r.field) : [];
    const mappedCategoryField = profile.Category;

    return Object.entries(profile.CategoryMappings).map(([key, value]) => {
        return {
            SourceField: `"${value}"`,
            TargetField: mappedCategoryField,
            Condition: {
                Field: (escapeSpaces as any)(mappedCategoryField, headers),
                Operator: "equals",
                Value: `"${key}"`
            }
        }
    });
}

function escapeSpacesInCondition(condition: any, headers: string[]): any {
    if (!condition) return condition;
    if (condition.Conditions && Array.isArray(condition.Conditions)) {
        return {
            ...condition,
            Conditions: condition.Conditions.map((sub: any) => escapeSpacesInCondition(sub, headers))
        };
    } else {
        return {
            ...condition,
            Field: (escapeSpaces as any)(condition.Field, headers),
        };
    }
}

export const formatProfileAttributeRulesToRules = (profile: ImportProfile, parsedFields: ParsedField[]) => {
    if(!profile.AttributeMappingRules || profile.AttributeMappingRules.length === 0) return [];

    const headers = parsedFields ? parsedFields.map(r => r.field) : [];

    return profile.AttributeMappingRules.map((rule) => {
        return {
            SourceField: rule.SourceField,
            TargetField: rule.TargetField,
            Condition: escapeSpacesInCondition(rule.Condition, headers),
            Type: "AdvancedAttributeRule"
        }
    });
}

// Sentinel distinct from any column name AND from undefined, so the SKU-missing
// error fires once even when sku is unmapped (supplierMappedSku === undefined).
const NO_SKU_ERROR = Symbol("noSkuError");
function Review(props: ReviewProps) {
    const {backStep, setActiveStep, steps} = props;
    const {profile, profileType} = props;
    const {formattedProfile} = props;
    const {parsedFields, parsedFieldsWithData} = props;
    const {file} = props;
    const {error, setError} = props;
    const {updated, setUpdated, updating, setUpdating, isMarketplace} = props;
    const {columnDataLoading} = props;

    const history = useHistory();
    const theme = useTheme();

    const [duplicateSkus, setDuplicateSkus] = React.useState<string[]>([]);
    const [importQueued, setImportQueued] = React.useState<boolean>(false);
    const [importStatus, setImportStatus] = React.useState<ImportStatus>({
        Complete: false,
        ImportedRecords: 0,
        FailedRecords: 0,
        ErrorMessage: "",
        ErrorReport: ""
    });
    const [importStatusTimer, setImportStatusTimer] = React.useState<number>(0);
    let statusStepInterval: any = null;

    //region Steps Display
    const CategoryMappingErrorMsg = (props: {setActiveStep: (s: number) => void; errors: any[]}) => {
        const {setActiveStep, errors} = props;

        return (
            <>
                Categories were mapped but there were {errors.length} error(s). To return to the mapping screen,
                click <span style={{color: "#0000FF", cursor: "pointer"}} onClick={() => setActiveStep(2)}>here</span>.
            </>
        )
    }

    const AttributeMappingErrorMsg = (props: {setActiveStep: (s: number) => void; errors: any[]}) => {
        const {setActiveStep, errors} = props;

        return (
            <>
                Attributes were mapped but there were {errors.length} issue(s). To return to the mapping screen,
                click <span style={{color: "#0000FF", cursor: "pointer"}} onClick={() => setActiveStep(3)}>here</span>.
            </>
        )
    }

    const reviewSteps = [
        {
            label: steps[0].errors
                ? ""
                : `File ${file?.name} added ${profile.Partner ? ` for ${profile.Partner}` : ""}`, status: steps[0].status
        },
        {
            label: steps[1].errors
                ? ""
                : "Category Column Selected", status: steps[1].status
        },
        {
            label: steps[2].errors
                ? <CategoryMappingErrorMsg setActiveStep={setActiveStep} errors={steps[2].errors!}/>
                : "Categories Mapped", status: steps[2].status
        },
        {
            label: steps[3].errors
                ? <AttributeMappingErrorMsg setActiveStep={setActiveStep} errors={steps[3].errors!}/>
                : "Attributes Mapped", status: steps[3].status
        },
        {
            label: steps[4].errors
                ? ""
                : "Lists Mapped", status: steps[4].status
        },
    ];
    //endregion

    const importCatalog = async () => {
        setUpdating(true);

        const columns = formatProfileToColumns(profile, parsedFields);
        const listRules = formatProfileListMappingsToRules(profile, parsedFields);
        const categoryRules = formatProfileCategoryMappingsToRules(profile, parsedFields);
        const attributeRules = formatProfileAttributeRulesToRules(profile, parsedFields);

        let postData = {
            Columns: columns,
            Rules: [...listRules, ...categoryRules, ...attributeRules]
        }

        try {
            let runStatus: any = null;
            if(props.isRetailer && profile.Partner) {
                runStatus = await (importSupplierProducts as any)(postData, profile.Partner);
            }
            else if(!props.isRetailer) {
                runStatus = await (importSupplierProducts as any)(postData, profile.Partner);
            }
            else {
                runStatus = await (importRetailerProducts as any)(postData);
            }

            if (runStatus.errorMessage) {
                setError({
                    open: true,
                    errorHeader: "Error importing catalog",
                    errorMessage: runStatus.errorMessage
                });
                return;
            } else {
                const importId = runStatus.split("?").pop().split("=").pop();

                let formData = new FormData();
                formData.append("file", file as any);

                try {
                    await axios.post(runStatus, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    setUpdated(true);
                    setImportQueued(true);
                    statusStepInterval = setInterval(async () => {
                        let count = 0;
                        setImportStatusTimer((prev) => {
                            count = prev;
                            return prev + 1
                        });
                        if (count >= 15 || count === 0) {
                            setImportStatusTimer(1);
                            let status: any = null;
                            if (props.isRetailer && profile.Partner) {
                                status = await (importSupplierProductsStatus as any)(profile.Partner, importId);
                            }
                            else if (!props.isRetailer) {
                                status = await (importSupplierProductsStatus as any)(profile.Partner, importId);
                            }
                            else {
                                status = await (importRetailerProductsStatus as any)(importId);
                            }

                            if (status.errorMessage) {
                                setError({
                                    open: true,
                                    errorHeader: "Error getting import status",
                                    errorMessage: status.errorMessage
                                });
                                clearInterval(statusStepInterval);
                                setUpdating(false);
                                (setUpdated as any)(false);
                                return;
                            } else {
                                setImportStatus(status);
                                if (status.Complete === true) clearInterval(statusStepInterval);
                            }
                        }
                    }, 1000);
                } catch (error: any) {
                    let errorMessage = error.message;
                    if (error.response != null) errorMessage = error.response.data.Message;
                    setError({
                        open: true,
                        errorHeader: "Error uploading import!",
                        errorMessage: errorMessage
                    });
                }
            }
        } catch (e: any) {
            let errorMessage = (error as any)?.message;
            if ((error as any)?.response != null) errorMessage = (error as any).response.data.Message;
            setError({
                open: true,
                errorHeader: "Error uploading import!",
                errorMessage: errorMessage
            });
        }
        setUpdating(false);
    }

    // Fire the error/warning only when the condition changes, so a dismissed dialog won't re-open.
    const lastSkuMissingRef = useRef<string | symbol | undefined>(NO_SKU_ERROR);
    const lastDupKeyRef = useRef<string | null>(null);
    // Re-runs as deferred duplicate values arrive after mount.
    useEffect(() => {
        // Get sku mapping
        const supplierMappedSku = profile.AttributeMappings?.["sku"]
        const foundSku: any = parsedFieldsWithData.find((field: any) => field.field === supplierMappedSku);
        if (!foundSku) {
            lastDupKeyRef.current = null;
            setDuplicateSkus((prev) => (prev.length ? [] : prev));
            if (lastSkuMissingRef.current !== supplierMappedSku) {
                lastSkuMissingRef.current = supplierMappedSku;
                setError({
                    open: true,
                    errorHeader: "Error mapping SKU",
                    errorMessage: "SKU field not found in the uploaded file. Please map the SKU field and try again."
                });
            }
            return;
        }
        lastSkuMissingRef.current = NO_SKU_ERROR;
        const dupKey = JSON.stringify(foundSku.duplicates || []);
        if(foundSku.duplicates && foundSku.duplicates.length > 0) {
            if (lastDupKeyRef.current !== dupKey) {
                lastDupKeyRef.current = dupKey;
                setError({
                    open: true,
                    errorHeader: "Duplicate SKUs Found",
                    errorMessage: `Duplicate SKUs found in the uploaded file. Please review the following SKUs: ${foundSku.duplicates.join(", ")}\n\nIf no action is taken, the last SKU on the file will be imported.`
                });
                setDuplicateSkus(foundSku.duplicates);
            }
        } else {
            lastDupKeyRef.current = null;
            // Clear stale warning when dupes vanish; no-op when empty to avoid a render.
            setDuplicateSkus((prev) => (prev.length ? [] : prev));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parsedFieldsWithData, profile.AttributeMappings]);

    useEffect(() => {
        return () => {
            clearInterval(statusStepInterval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container style={{gap: pageContentGap, width: "100%"}}>
            <>
                {steps.some((step: any) => step.errors) && (
                    <Card id="cueMessage" style={{backgroundColor: '#ee3224', color: 'white', width: "100%"}}>
                        <CardContent style={{display: 'flex', alignItems: 'center'}}>
                            <ErrorIcon style={{marginRight: '1rem'}}/>
                            <Typography style={{
                                textAlign: "left",
                                overflowY: "auto",
                                overflowX: "hidden",
                                maxHeight: "100px"
                            }}>
                                One or more steps have errors or warnings, please review before continuing.
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {duplicateSkus.length > 0 && (
                    <Card id="cueMessage" style={{backgroundColor: '#ee3224', color: 'white', width: "100%"}}>
                        <CardContent style={{display: 'flex', alignItems: 'center'}}>
                            <ErrorIcon style={{marginRight: '1rem'}}/>
                            <Typography style={{
                                textAlign: "left",
                                overflowY: "auto",
                                overflowX: "hidden",
                                maxHeight: "200px"
                            }}>
                                Duplicate SKUs found in the uploaded file. Please review the following SKUs: {duplicateSkus.join(", ")}
                                <br />
                                If no action is taken, the last SKU on the file will be imported.
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </>
            <Paper style={{width: "100%", padding: "24px", borderRadius: "5px"}}>
                <Grid container>
                    <SectionTitle>
                        Review
                    </SectionTitle>
                </Grid>

                <Grid container>
                    <EnhancedStepper
                        steps={reviewSteps} activeStep={-1} vertical
                        iconstyles={{width: "10px", height: "10px"}}
                        stepstyles={{color: "black", fontWeight: 400}}
                    />
                </Grid>
            </Paper>
            <Grid container justifyContent={"flex-end"} style={{gap: "8px", width: "100%"}}>
                <Button variant="outlined" disabled={updating} style={{justifySelf: "flex-end"}} onClick={() => backStep()}>
                    Back
                </Button>

                <Button variant="contained" color="primary"
                    disabled={updating || importQueued || columnDataLoading || steps.some((step: any) => step.status === "error")}
                    style={{justifySelf: "flex-end"}}
                    onClick={() => {
                        (SaveProfile as any)(formattedProfile, profileType);
                        importCatalog();
                    }}
                >
                    {updating && (<CircularProgress
                        style={{width: "20px", height: "20px", marginRight: "10px", color: "blue"}}/>)}
                    {props.isRetailer
                        ? "Save & Submit"
                        : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                            ? "Publish"
                            : "Save & Submit"
                    }
                </Button>
            </Grid>
            <Dialog
                open={updated}
                onClose={(event, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                }}
                slotProps={{
                    paper: {
                        style: {
                            borderRadius: "12px"
                        }
                    }
                }}>
                <DialogTitle style={{padding: "16px 24px 0 24px"}}>
                    <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 1, flexDirection: "column"}}>
                            <DialogHeader>
                                Files uploaded, import queued
                            </DialogHeader>
                        </Grid>
                    </Grid>
                </DialogTitle>

                <DialogContent style={{padding: "0 24px 1rem 24px"}}>
                    <Typography variant={"body1"}>
                        Your files have been uploaded and queued, please wait while they are processed.
                    </Typography>

                    <Grid container alignItems={"center"} style={{marginTop: "12px", gap: pageContentGap}}>
                        <SectionTitle>
                            {importStatus?.Complete === false
                                ? `Checking import status in ${15 - importStatusTimer} seconds...`
                                : (importStatus?.ErrorMessage || importStatus?.FailedRecords > 0)
                                    ? "Files processed with errors!"
                                    : <>
                                        Files Processed, please review in {" "}
                                        {props.isRetailer && props.urlPartnerId
                                            ? <Link style={{color: (theme.palette as any).tertiary?.main}} href={"/productonboardingcenter/catalog?catalog=partnercatalog&q_order=desc&q_orderby=lastUpdate"}>Supplier Catalog</Link>
                                            : <Link style={{color: (theme.palette as any).tertiary?.main}} href={"/productonboardingcenter/catalog?catalog=mycatalog&q_order=desc&q_orderby=lastUpdate"}>My Catalog</Link>
                                        }
                                    </>
                            }
                        </SectionTitle>

                        {!importStatus?.Complete && (
                            <CircularProgress style={{width: "20px", height: "20px", color: "blue"}}/>
                        )}
                    </Grid>

                    <Grid container style={{textAlign: "start"}}>
                        <Grid item size={12}>
                            <Typography>
                                Completed: {importStatus?.Complete === true ? "Yes" : "No"}
                            </Typography>
                        </Grid>

                        <Grid item size={12}>
                            <Typography>
                                Records Imported: {importStatus?.ImportedRecords}
                            </Typography>
                        </Grid>

                        <Grid item size={12}>
                            <Typography>
                                Records Failed: {importStatus?.FailedRecords}
                            </Typography>
                        </Grid>

                        <Grid item size={12}>
                            <Typography>
                                File Errors: {importStatus?.ErrorMessage ?? "None"}
                            </Typography>
                        </Grid>

                        <Grid item size={12}>
                            <Typography>
                                Error Report: {importStatus?.ErrorReport ? (
                                <Link
                                    sx={(theme: any) => ({
                                        color: theme.palette.tertiary?.main || theme.palette.primary.main,
                                        textDecoration: "none",
                                        "&:hover": { textDecoration: "underline" }
                                    })}
                                    style={{textAlign: "left"}}
                                    target="_blank" rel="noreferrer"
                                    href={(updateImportLinkTarget as any)(importStatus?.ErrorReport ?? "")}
                                >
                                    report
                                </Link>
                            ) : "None"}
                            </Typography>
                        </Grid>

                        {importStatus?.Complete === true && (
                            <Grid item size={12} style={{marginTop: "12px"}}>
                                {(importStatus?.ErrorMessage || importStatus?.FailedRecords > 0) ? (
                                    <Typography>
                                        {importStatus?.ErrorMessage
                                            ? "There were errors parsing the upload file, please review 'File Errors' above and re-upload the file."
                                            : "There were row errors within the uploaded file, please review the error report CSV above and re-upload the file."}
                                    </Typography>
                                ) : (
                                    <Typography>
                                        Import complete!
                                    </Typography>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    {importStatus?.Complete && (importStatus?.ErrorMessage || importStatus?.FailedRecords > 0) ? (
                        <>
                            {importStatus?.ErrorReport && (
                                <Button variant="outlined"
                                    {...({target: "_blank", rel: "noreferrer", href: (updateImportLinkTarget as any)(importStatus?.ErrorReport ?? "")} as any)}
                                >
                                    Download Error Report
                                </Button>
                            )}

                            <Button variant="outlined"
                                onClick={() => {
                                    setUpdated(false);
                                    setActiveStep(0);
                                }}>
                                Return to Product Import
                            </Button>
                        </>
                    ) : (
                        <Button variant="contained" color="primary"
                            onClick={() => {
                                const returnUrl = props.isRetailer && props.urlPartnerId
                                    ? "/productonboardingcenter/catalog?catalog=partnercatalog&q_order=desc&q_orderby=lastUpdate"
                                    : "/productonboardingcenter/catalog?catalog=mycatalog&q_order=desc&q_orderby=lastUpdate";

                                if (importStatus?.Complete) {
                                    history.push(returnUrl);
                                    return;
                                }

                                const confirmed = window.confirm("Are you sure you want to leave, once you leave you won't be able to monitor import status!")
                                if (confirmed) {
                                    history.push(returnUrl);
                                }
                            }}>
                            {props.isRetailer && props.urlPartnerId ? "Return to Supplier Catalog" : "Return to My Catalog"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default Review;
